/**
 * Application Constants
 * Single source of truth for all hardcoded values used throughout the app
 */

// ===========================
// POSITION CONSTANTS
// ===========================
export const POSITIONS = ['GKP', 'DEF', 'MID', 'FWD'];

export const POSITION_LABELS = {
  GKP: 'Goalkeeper',
  DEF: 'Defender',
  MID: 'Midfielder',
  FWD: 'Forward',
};

export const POSITION_IDS = {
  GKP: 1,
  DEF: 2,
  MID: 3,
  FWD: 4,
};

// ===========================
// SQUAD CONSTANTS
// ===========================
export const MAX_STARTERS = 11;
export const SQUAD_SIZE = 15;
export const BENCH_START = MAX_STARTERS + 1;

// ===========================
// SCORING THRESHOLDS
// ===========================
export const USEFUL_SCORER_MIN_POINTS = 4;
export const RETURN_PREDICTION_THRESHOLD = 4;
export const BLANK_PREDICTION_THRESHOLD = 4;
export const HIGHLIGHT_PREDICTION_THRESHOLD = 5; // For highlighting high-value predictions

// Defensive contribution thresholds (for progress bars)
export const DEFENSIVE_CONTRIBUTION = {
  DEF_THRESHOLD: 10,  // Defenders need 10 for 100% bar
  MID_THRESHOLD: 12,  // Midfielders need 12 for 100% bar
};

// ===========================
// UI CONSTANTS
// ===========================
export const UI = {
  borderRadius: {
    none: 0,      // No rounding
    xs: 1,        // Extra small - minimal rounding
    sm: 2,        // Small - subtle rounding
    small: 3,     // Small - default subtle rounding (legacy)
    md: 4,        // Medium-small - moderate rounding
    medium: 8,    // Medium - noticeable rounding (legacy)
    lg: 12,       // Large - prominent rounding
    large: 16,    // Large - very prominent rounding (legacy)
    circle: '50%', // Circular elements
  },
  border: '1px solid #DADADA',
  zIndex: {
    // Sticky table elements
    stickyHeader: 3,    // Sticky table header (top row)
    stickyColumn: 2,    // Sticky table column (first column)
    base: 1,            // Base layer
    // Overlays and interactions
    overlay: 1000,      // Modal/dialog overlays
    dragging: 999,      // Dragging elements
    // Legacy aliases (for backwards compatibility)
    header: 3,
    column: 2,
  },
  fontSize: {
    // Primary scale (most commonly used)
    xs: 10, // Extra small - chips, tiny labels
    sm: 11, // Small - chart ticks, position labels
    base: 12, // Base - table bodies, body text (DEFAULT)
    md: 14, // Medium - table headers, headings, emphasis
    lg: 16, // Large - section headings
    xl: 18, // Extra large - main headings
    xxl: 24, // Double extra large - scores, hero numbers

    // Legacy aliases (for backwards compatibility)
    tableBody: 12,
    tableHeader: 14,

    // Special sizes
    caption: 11, // Captions and secondary text
    subheading: 13, // Slightly larger than base
  },
  drawerWidth: 360,
  appBarHeight: 65,
  height: {
    // Icons and avatars
    icon: {
      xs: 16,      // Extra small icons
      sm: 20,      // Small icons
      md: 24,      // Medium icons (default)
      lg: 40,      // Large icons/avatars
    },
    // Chart containers
    chart: {
      small: 305,          // ResponsiveContainer for small charts
      smallWrapper: 310,   // Paper wrapper for small charts
      medium: 395,         // ResponsiveContainer for medium charts (treemap)
      mediumWrapper: 400,  // Paper wrapper for medium charts (treemap)
    },
    // Table max heights
    table: {
      small: 500,   // Compact tables
      medium: 550,  // Medium tables
      large: 600,   // Large scrollable tables
      xlarge: 700,  // Extra large scrollable tables
    },
    // UI elements
    progressBar: 18,
    miniBar: 5,
    tableCell: 30,
    accordionSummary: 48,
    // Layout
    minContent: 200,  // Minimum content height for loading states
  },
};

// ===========================
// COLOR PALETTE
// ===========================
export const COLORS = {
  // Base colors
  border: '#DADADA',
  white: '#FFF',
  lightBg: 'rgba(255,255,255,0.19)',
  lightBlueBg: 'rgba(0,149,255,0.05)', // Light blue selection/hover background
  darkNavy: '#0C213C',

  // Grey scale
  grey: {
    50: '#fafafa',   // Lightest grey - backgrounds
    100: '#f5f5f5',  // Very light grey - backgrounds
    200: '#eeeeee',  // Light grey (#eee) - backgrounds
    300: '#e0e0e0',  // Light grey - dividers, disabled states
    400: '#ccc',     // Medium-light grey - borders
    500: '#999',     // Medium grey - secondary text
    600: '#888888',  // Medium-dark grey - secondary text
    700: '#666',     // Dark grey - text
    800: '#333',     // Very dark grey - text
    900: '#222',     // Near black - text
    black: '#000',   // Pure black
  },

  text: {
    primary: '#262626',
    secondary: '#888888',
  },

  // Feature colors
  buttonActive: '#0095FF',
  pitchGreen: '#407a45',
  defaultChart: '#888888',

  // Status colors
  error: '#E15759',
  success: '#59A14F',
  warning: '#F28E2B',
};

// Chart color palette (used for multi-series charts)
export const CHART_COLORS = [
  '#4E79A7',
  '#F28E2B',
  '#E15759',
  '#76B7B2',
  '#59A14F',
  '#EDC948',
  '#B07AA1',
  '#FF9DA7',
  '#9C755F',
  '#BAB0AC',
];

// ===========================
// PITCH CONSTANTS
// ===========================
export const PITCH = {
  // Container dimensions
  maxWidth: 400, // Mobile-first max width
  borderRadius: 4,

  // Player card dimensions
  card: {
    width: 70, // Max width to fit 5 across with gaps
    shirtSize: 40, // Fixed shirt/badge size
    gap: 8, // Gap between cards
    borderRadius: 1
  },

  // Colors
  colors: {
    green: '#407a45', // Main pitch background
    opponent: '#8b4513', // Opponent pitch background (brown)
    cardBackground: '#FCFFF9', // Light cream card background
    cardHeader: '#0C213C', // Dark navy header
    cardHeaderText: '#FFF', // White text on header
    badge: 'rgba(252,255,249,0.19)', // Semi-transparent badge backgrounds
    defensiveContribution: '#1072CA', // Blue DC progress bar
    progressBarBg: '#e0e0e0', // Grey DC bar background
    dragBorder: '1px dotted #FFF', // Drag border style
    dragHover: 'rgba(255,255,255,0.3)', // Drag hover state
  },

  // Font sizes (per row type in player cards)
  fontSize: {
    playerName: 11,
    position: 11,
    teamName: 16,
    vsText: 14,
    odds: 12,
    fixtures: 10,
    managerName: 10,
    score: 24,
    points: 11,
  },
};

// ===========================
// API CONSTANTS
// ===========================
export const API = {
  timeout: 30000,
  slowRequestThreshold: 5000,
  pollTimeout: 10000,
};

// ===========================
// DATE CONSTANTS
// ===========================
export const SEASON_CUTOFF_DATE = '2025-08-17T00:00:00Z';

// ===========================
// MAGIC NUMBERS (DOCUMENTED)
// ===========================
// Defensive score thresholds for auto-substitution logic
export const DEFENSIVE_THRESHOLD = {
  DEF: 10, // Defensive score threshold for defenders
  MID: 12, // Defensive score threshold for midfielders
};
